import { useRef } from "react";
import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";
import { get } from "lodash";
import { FileAudio } from "lucide-react";

import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
} from "@/components/ui/attachment";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";

/**
 * Picks the system voicemail recording.
 *
 * The chosen `File` is held in the form and uploaded on save, so a file that
 * is picked and then abandoned never reaches S3. Until then the field holds
 * the saved attachment record, or null when no voicemail is configured.
 */
export function VoicemailFileField({ name, label, disabled }) {
  const inputRef = useRef(null);
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const isPending = field.value instanceof File;
        const fileName = isPending ? field.value.name : field.value?.file_name;
        return (
          <Field data-invalid={error ? true : undefined}>
            <FieldLabel htmlFor={name}>{label}</FieldLabel>
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
              <Attachment state={fileName ? "done" : "idle"} size="sm">
                <AttachmentMedia>
                  <FileAudio />
                </AttachmentMedia>
                <AttachmentContent>
                  <AttachmentTitle>
                    {fileName ?? "No file selected"}
                  </AttachmentTitle>
                  {isPending && (
                    <AttachmentDescription>
                      Uploads on save
                    </AttachmentDescription>
                  )}
                </AttachmentContent>
              </Attachment>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
              >
                {fileName ? "Replace" : "Choose file"}
              </Button>
            </div>
            <input
              ref={inputRef}
              id={name}
              type="file"
              accept=".mp3"
              // Field widens every direct child to full width and exempts only
              // `.sr-only`, not our prefixed `tw:sr-only`, so a visually hidden
              // input would overflow the page horizontally.
              hidden
              aria-invalid={error ? true : undefined}
              onChange={event => {
                const file = event.target.files?.[0];
                // Reset so choosing the same file again still fires onChange.
                event.target.value = "";
                if (file) field.onChange(file);
              }}
            />
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
}

VoicemailFileField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
};
